import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  flashSaleService,
  FlashSale,
  Product
} from "../services/flashSaleService";

// =====================================================
// TOAST MODAL — alert replacement
// =====================================================
function ToastModal({
  message,
  type,
  onClose,
}: {
  message: string;
  type: "success" | "error" | "info";
  onClose: () => void;
}) {
  const icon =
    type === "success" ? "✅" :
    type === "error"   ? "❌" : "ℹ️";
  const color =
    type === "success" ? "#166534" :
    type === "error"   ? "#991b1b" : "#1e3a5f";
  const bg =
    type === "success" ? "#dcfce7" :
    type === "error"   ? "#fee2e2" : "#dbeafe";

  return (
    <div
      className="pm-del-overlay"
      style={{ zIndex: 99999 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="pm-del-box" style={{ maxWidth: 380 }}>
        <div
          className="pm-del-ico"
          style={{ background: bg }}
        >
          <span style={{ fontSize: 24 }}>{icon}</span>
        </div>
        <div
          className="pm-del-title"
          style={{ color }}
        >
          {message}
        </div>
        <div className="pm-del-btns" style={{ marginTop: 22 }}>
          <button
            className="pm-btn-save"
            onClick={onClose}
            type="button"
            style={{ minWidth: 100 }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

type ModalMode = "none" | "add" | "edit";

const emptyForm = () => ({
  title: "",
  discount: 10,
  productIDs: [] as number[],
  startTime: "",
  endTime: "",
  status: "upcoming" as FlashSale["status"],
  banner: "⚡",
  saleLabel: "",
  saveUpTo: 40,
  saleImageUrl: "",
  saleImageFile: null as File | null,
  isActive: true
});

export function FlashSalesPage() {

  // =====================================================
  // STATES
  // =====================================================

  const [sales, setSales] =
    useState<FlashSale[]>([]);

  const [products, setProducts] =
    useState<Product[]>([]);

  const [modalMode, setModalMode] =
    useState<ModalMode>("none");

  const [selected, setSelected] =
    useState<FlashSale | null>(null);

  const [form, setForm] =
    useState(emptyForm());

  const [loading, setLoading] =
    useState(false);

  const imageRef =
    useRef<HTMLInputElement>(null);

  const prodDropRef =
    useRef<HTMLDivElement>(null);

  const portalDropRef =
    useRef<HTMLDivElement>(null);

  const triggerRef =
    useRef<HTMLDivElement>(null);

  const [dropPos, setDropPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const [prodDropOpen, setProdDropOpen] =
    useState(false);

  const [prodSearch, setProdSearch] =
    useState("");

  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteId, setDeleteId] =
    useState<number | null>(null);

  // Toast modal state
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  function showToast(
    message: string,
    type: "success" | "error" | "info" = "success"
  ) {
    setToast({ message, type });
  }
  // =====================================================
  // LOAD DATA
  // =====================================================

  useEffect(() => {

    loadFlashSales();

    loadProducts();

  }, []);

  async function loadFlashSales() {

    try {

      setLoading(true);

      const data =
        await flashSaleService.getAll();

      setSales(data);

    } catch (error) {

      console.error(error);
    }
    finally {

      setLoading(false);
    }
  }

  async function loadProducts() {

    try {

      const data =
        await flashSaleService.getProducts();

      setProducts(data);

    } catch (error) {

      console.error(error);
    }
  }

  // =====================================================
  // OUTSIDE CLICK
  // =====================================================

  useEffect(() => {

    function handle(e: MouseEvent) {

      const target = e.target as Node;

      // Trigger div ke andar click — ignore
      if (prodDropRef.current?.contains(target)) return;

      // Portal dropdown ke andar click — ignore
      if (portalDropRef.current?.contains(target)) return;

      // Bahar click — band karo
      setProdDropOpen(false);
    }

    document.addEventListener(
      "mousedown",
      handle
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        handle
      );

  }, []);

  // =====================================================
  // FORM SETTER
  // =====================================================

  function f<
    K extends keyof ReturnType<
      typeof emptyForm
    >
  >(k: K, v: any) {

    setForm(p => ({
      ...p,
      [k]: v
    }));
  }

  // =====================================================
  // OPEN ADD
  // =====================================================

  function openAdd() {

    setSelected(null);

    setForm(emptyForm());

    setModalMode("add");
  }

  // =====================================================
  // OPEN EDIT
  // =====================================================

  function openEdit(s: FlashSale) {

    setSelected(s);

    setForm({

      title: s.title,

      discount: s.discount,

      productIDs:
        s.products?.map(
          x => x.productID
        ) || [],

      startTime: s.startTime,

      endTime: s.endTime,

      status: s.status,

      banner: s.banner,

      saleLabel:
        s.saleLabel || "",

      saveUpTo:
        s.saveUpTo,

      saleImageUrl:
        s.saleImageUrl || "",

      saleImageFile: null,

      isActive: s.isActive
    });

    setModalMode("edit");
  }

  // =====================================================
  // CLOSE MODAL
  // =====================================================

  function closeModal() {

    setModalMode("none");

    setSelected(null);

    setProdDropOpen(false);

    setProdSearch("");
  }

  // =====================================================
  // TOGGLE PRODUCT
  // =====================================================

  function toggleProduct(id: number) {

    setForm(p => ({

      ...p,

      productIDs:
        p.productIDs.includes(id)

          ? p.productIDs.filter(
            x => x !== id
          )

          : [...p.productIDs, id]
    }));
  }

  // =====================================================
  // IMAGE
  // =====================================================

  function handleImageFile(
    e: React.ChangeEvent<HTMLInputElement>
  ) {

    const file =
      e.target.files?.[0];

    if (!file) return;

    f("saleImageFile", file);

    const reader =
      new FileReader();

    reader.onload = ev => {

      f(
        "saleImageUrl",
        ev.target?.result as string
      );
    };

    reader.readAsDataURL(file);
  }

  // =====================================================
  // SAVE
  // =====================================================

  async function save() {

    try {

      if (!form.title.trim()) {

        showToast("Title required", "error");

        return;
      }

      if (
        form.productIDs.length === 0
      ) {

        showToast(
          "Select at least one product",
          "error"
        );

        return;
      }

      setSaveLoading(true);

      if (
        modalMode === "add"
      ) {

        await flashSaleService.create(
          form
        );

        showToast(
          "Flash sale created successfully",
          "success"
        );
      }
      else if (
        modalMode === "edit" &&
        selected
      ) {

        await flashSaleService.update(
          selected.flashSaleID,
          form
        );

        showToast(
          "Flash sale updated successfully",
          "success"
        );
      }

      await loadFlashSales();

      closeModal();

    } catch (error: any) {

      console.error(error);

      showToast(
        error?.message ||
        "Save failed",
        "error"
      );
    }
    finally {

      setSaveLoading(false);
    }
  }

  // =====================================================
  // DELETE
  // =====================================================

  async function deleteSale() {

    if (deleteId === null) return;

    try {

      setDeleteLoading(true);

      await flashSaleService.delete(deleteId);

      await loadFlashSales();

      setDeleteId(null);

      showToast("Flash sale deleted successfully", "success");

    } catch (error) {

      console.error(error);

      showToast("Delete failed", "error");

    } finally {

      setDeleteLoading(false);
    }
  }

  // =====================================================
  // COUNTDOWN
  // =====================================================

  function getCountdown(
    endTime: string
  ) {

    const diff =
      new Date(endTime).getTime()
      - Date.now();

    if (diff <= 0)
      return "Ended";

    const h =
      Math.floor(diff / 3600000);

    const m =
      Math.floor(
        (diff % 3600000)
        / 60000
      );

    const s =
      Math.floor(
        (diff % 60000)
        / 1000
      );

    return `${h}h ${m}m ${s}s`;
  }

  // =====================================================
  // STATUS STYLE
  // =====================================================

  const statusStyle = {

    live: {
      bg: "#dcfce7",
      color: "#166534",
      dot: "#22c55e"
    },

    upcoming: {
      bg: "#dbeafe",
      color: "#1d4ed8",
      dot: "#3b82f6"
    },

    ended: {
      bg: "#f1f5f9",
      color: "#475569",
      dot: "#94a3b8"
    }
  };

  // =====================================================
  // FILTER PRODUCTS
  // =====================================================

  const filteredProducts =
    products.filter(x =>
      x.productName
        .toLowerCase()
        .includes(
          prodSearch.toLowerCase()
        )
    );

  return (
    <>

      {/* ================================================= */}
      {/* TOPBAR */}
      {/* ================================================= */}

      <div className="fs-topbar">

        <div className="fs-info-text">

          {
            sales.filter(
              s => s.status === "live"
            ).length
          }

          {" "}live ·{" "}

          {
            sales.filter(
              s => s.status === "upcoming"
            ).length
          }

          {" "}upcoming
        </div>

        <button
          className="fs-btn-primary"
          onClick={openAdd}
        >
          ⚡ Create Flash Sale
        </button>
      </div>

      {/* ================================================= */}
      {/* CARDS */}
      {/* ================================================= */}

      {loading ? (

        <div>Loading...</div>

      ) : (

        sales.map(s => (

          <div
            key={s.flashSaleID}
            className={`fs-card ${s.status}`}
          >

            <div className="fs-card-inner">

              {/* LEFT */}

              <div className="fs-card-left">

                {s.saleImageUrl ? (

                  <>
                    <img
                      src={
                        s.saleImageUrl
                          ? `${process.env.REACT_APP_API_PATH}/Uploads/${s.saleImageUrl}`
                          : "/no-image.png"
                      }
                      alt={s.title}
                      onError={(e) => {
                        e.currentTarget.src = "/no-image.png";
                      }}
                    />

                    <div className="fs-sale-overlay">

                      <div className="fs-sale-label">
                        {s.saleLabel}
                      </div>

                      <div className="fs-sale-pct">
                        {s.saveUpTo}% OFF
                      </div>

                    </div>
                  </>

                ) : (

                  <div className="fs-card-left-placeholder">

                    <div className="fs-card-left-placeholder-icon">
                      {s.banner}
                    </div>

                    <div className="fs-no-img-pct">
                      {s.saveUpTo}%
                    </div>

                    <div className="fs-no-img-off">
                      OFF
                    </div>

                  </div>
                )}
              </div>

              {/* RIGHT */}

              <div className="fs-card-right">

                <div className="fs-actions">

                  <button
                    className="fs-btn-sm"
                    onClick={() =>
                      openEdit(s)
                    }
                  >
                    Edit
                  </button>

                  <button
                    className="fs-btn-sm fs-btn-del"
                    onClick={() =>
                      setDeleteId(
                        s.flashSaleID
                      )
                    }
                    type="button"
                  >
                    Delete
                  </button>
                </div>

                <div
                  className="fs-badge"
                  style={{
                    background:
                      statusStyle[s.status].bg,
                    color:
                      statusStyle[s.status].color
                  }}
                >
                  {s.status}
                </div>

                <div className="fs-title">
                  {s.title}
                </div>

                <div className="fs-discount">
                  {s.discount}% OFF
                </div>

                {s.status === "live" && (

                  <div className="fs-countdown">
                    ⏱️ Ends in:
                    <strong>
                      {" "}
                      {
                        getCountdown(
                          s.endTime
                        )
                      }
                    </strong>
                  </div>
                )}

                <div className="fs-products">

                  {s.products?.map(p => (

                    <span
                      key={p.productID}
                      className="fs-prod"
                    >
                      💍 {p.productName}
                    </span>

                  ))}
                </div>

              </div>
            </div>
          </div>
        ))
      )}

      {/* ================================================= */}
      {/* MODAL */}
      {/* ================================================= */}

      {/* =========================================================
   CREATE / EDIT FLASH SALE MODAL
========================================================= */}

      {(modalMode === "add" || modalMode === "edit") && (
        <div
          className="ns-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="ns-modal">

            {/* =========================================
          HEADER
      ========================================= */}
            <div className="ns-modal-head">
              <div className="ns-head-left">
                <span className="ns-head-icon">⚡</span>

                <span className="ns-modal-title">
                  {modalMode === "add"
                    ? "New Flash Sale"
                    : "Edit Flash Sale"}
                </span>
              </div>

              <button
                className="ns-modal-close"
                onClick={closeModal}
                type="button"
              >
                ✕
              </button>
            </div>

            {/* =========================================
          BODY
      ========================================= */}
            <div className="ns-modal-body">

              {/* SALE TITLE */}
              <div className="ns-field2">
                <label className="ns-label2">
                  SALE TITLE *
                </label>

                <input
                  className="ns-input2"
                  value={form.title}
                  onChange={(e) =>
                    f("title", e.target.value)
                  }
                  placeholder="e.g. Weekend Mega Sale"
                />
              </div>

              {/* ROW */}
              <div className="ns-row2">

                {/* DISCOUNT */}
                <div className="ns-field2">
                  <label className="ns-label2">
                    DISCOUNT (%)
                  </label>

                  <input
                    className="ns-input2"
                    type="number"
                    min={1}
                    max={90}
                    value={form.discount}
                    onChange={(e) =>
                      f("discount", Number(e.target.value))
                    }
                  />
                </div>

                {/* STATUS */}
                <div className="ns-field2">
                  <label className="ns-label2">
                    STATUS
                  </label>

                  <select
                    className="ns-input2"
                    value={form.status}
                    onChange={(e) =>
                      f(
                        "status",
                        e.target.value as FlashSale["status"]
                      )
                    }
                  >
                    <option value="upcoming">
                      Upcoming
                    </option>

                    <option value="live">
                      Live
                    </option>

                    <option value="ended">
                      Ended
                    </option>
                  </select>
                </div>
              </div>

              {/* ROW */}
              <div className="ns-row2">

                {/* START */}
                <div className="ns-field2">
                  <label className="ns-label2">
                    START TIME
                  </label>

                  <input
                    className="ns-input2"
                    type="datetime-local"
                    value={form.startTime}
                    onChange={(e) =>
                      f("startTime", e.target.value)
                    }
                  />
                </div>

                {/* END */}
                <div className="ns-field2">
                  <label className="ns-label2">
                    END TIME
                  </label>

                  <input
                    className="ns-input2"
                    type="datetime-local"
                    value={form.endTime}
                    onChange={(e) =>
                      f("endTime", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* =========================================
            PRODUCTS DROPDOWN
        ========================================= */}

              <div className="ns-field2">
                <label className="ns-label2">
                  PRODUCTS IN SALE
                </label>

                <div
                  ref={prodDropRef}
                  style={{ position: "relative" }}
                >
                  {/* TRIGGER */}
                  <div
                    ref={triggerRef}
                    className={`ns-input2 ns-product-trigger${prodDropOpen ? " open" : ""}`}
                    onClick={() => {
                      if (!prodDropOpen && triggerRef.current) {
                        const rect = triggerRef.current.getBoundingClientRect();
                        setDropPos({
                          top: rect.bottom + window.scrollY + 6,
                          left: rect.left + window.scrollX,
                          width: rect.width,
                        });
                      }
                      setProdDropOpen(!prodDropOpen);
                    }}
                  >
                    <span>
                      {form.productIDs.length === 0
                        ? "Select products..."
                        : `${form.productIDs.length} products selected`}
                    </span>

                    <span className={`ns-arrow${prodDropOpen ? " open" : ""}`}>
                      ▾
                    </span>
                  </div>

                  {/* DROPDOWN — Portal ke through body mein render hoga */}
                  {prodDropOpen && dropPos && ReactDOM.createPortal(
                    <div
                      ref={portalDropRef}
                      className="ns-product-dropdown"
                      style={{
                        position: "absolute",
                        top: dropPos.top,
                        left: dropPos.left,
                        width: dropPos.width,
                        zIndex: 999999,
                      }}
                    >

                      {/* SEARCH */}
                      <div className="ns-search-wrap">
                        <input
                          className="ns-search-input"
                          placeholder="Search products..."
                          value={prodSearch}
                          onChange={(e) =>
                            setProdSearch(e.target.value)
                          }
                        />
                      </div>

                      {/* PRODUCT LIST */}
                      <div className="ns-product-list">

                        {products
                          .filter((x) =>
                            x.productName
                              .toLowerCase()
                              .includes(
                                prodSearch.toLowerCase()
                              )
                          )
                          .map((p) => {
                            const checked =
                              form.productIDs.includes(
                                p.productID
                              );

                            return (
                              <label
                                key={p.productID}
                                className={`ns-product-item ${checked
                                  ? "active"
                                  : ""
                                  }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    toggleProduct(
                                      p.productID
                                    )
                                  }
                                />

                                <span>
                                  💍 {p.productName}
                                </span>
                              </label>
                            );
                          })}
                      </div>

                      {/* FOOTER */}
                      <div className="ns-product-footer">
                        <button
                          type="button"
                          className="ns-btn-orange-sm"
                          onClick={() =>
                            setProdDropOpen(false)
                          }
                        >
                          Done
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>

                {/* CHIPS */}
                {form.productIDs.length > 0 && (
                  <div className="ns-chip-wrap">
                    {products
                      .filter((x) =>
                        form.productIDs.includes(
                          x.productID
                        )
                      )
                      .map((p) => (
                        <div
                          key={p.productID}
                          className="ns-chip"
                        >
                          💍 {p.productName}

                          <button
                            type="button"
                            onClick={() =>
                              toggleProduct(
                                p.productID
                              )
                            }
                          >
                            ×
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* =========================================
            BANNER SECTION
        ========================================= */}

              <div className="ns-section-divider">
                🖼️ SALE BANNER CARD — LEFT SIDE IMAGE
              </div>

              <div className="ns-row2">

                {/* LABEL */}
                <div className="ns-field2">
                  <label className="ns-label2">
                    CATEGORY LABEL
                  </label>

                  <input
                    className="ns-input2"
                    value={form.saleLabel}
                    onChange={(e) =>
                      f("saleLabel", e.target.value)
                    }
                    placeholder="e.g. Earring"
                  />
                </div>

                {/* SAVE UP TO */}
                <div className="ns-field2">
                  <label className="ns-label2">
                    SAVE UP TO (%)
                  </label>

                  <input
                    className="ns-input2"
                    type="number"
                    min={1}
                    max={90}
                    value={form.saveUpTo}
                    onChange={(e) =>
                      f(
                        "saveUpTo",
                        Number(e.target.value)
                      )
                    }
                  />
                </div>
              </div>

              {/* IMAGE */}
              <div className="ns-field2">
                <label className="ns-label2">
                  BANNER IMAGE
                </label>

                <div className="fs-img-upload-box">

                  {form.saleImageUrl ? (
                    <div className="fs-img-preview-wrap">
                      <img
                        src={form.saleImageUrl}
                        alt="Preview"
                      />

                      <div className="fs-img-preview-overlay">
                        <button
                          type="button"
                          className="fs-img-overlay-btn"
                          onClick={() =>
                            imageRef.current?.click()
                          }
                        >
                          Change
                        </button>

                        <button
                          type="button"
                          className="fs-img-overlay-btn del"
                          onClick={() => {
                            f("saleImageUrl", "");
                            f("saleImageFile", null);

                            if (imageRef.current)
                              imageRef.current.value =
                                "";
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="fs-img-empty"
                      onClick={() =>
                        imageRef.current?.click()
                      }
                    >
                      <div className="fs-img-empty-icon">
                        🖼️
                      </div>

                      <div className="fs-img-empty-text">
                        Click to upload sale banner image
                      </div>
                    </div>
                  )}

                  <input
                    ref={imageRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleImageFile}
                  />
                </div>
              </div>
            </div>

            {/* =========================================
          FOOTER
      ========================================= */}
            <div className="ns-modal-foot">
              <button
                className="ns-btn-sec"
                onClick={closeModal}
                type="button"
              >
                Cancel
              </button>

              <button
                className="ns-btn-orange"
                onClick={save}
                type="button"
              >
                {modalMode === "add"
                  ? "Create Sale"
                  : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId !== null && (

        <div
          className="pm-del-overlay"
          onClick={(e) => {

            if (e.target === e.currentTarget) {

              setDeleteId(null);
            }
          }}
        >

          <div className="pm-del-box">

            <div className="pm-del-ico">

              🗑️

            </div>

            <div className="pm-del-title">

              Delete Flash Sale?

            </div>

            <div className="pm-del-sub">

              This action cannot be undone.

            </div>

            <div className="pm-del-btns">

              <button
                className="pm-btn-ghost"
                onClick={() =>
                  setDeleteId(null)
                }
                type="button"
              >
                Cancel
              </button>

              <button
                className="pm-btn-danger"
                onClick={deleteSale}
                type="button"
              >
                Delete
              </button>

            </div>

          </div>

        </div>
      )}

      {/* TOAST MODAL */}
      {toast && (
        <ToastModal
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}